import * as React from 'react';

import { cn } from '../lib/cn';

export interface CodeInputProps {
  length?: number;
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  className?: string;
  inputMode?: 'numeric' | 'text';
  id?: string;
  'aria-label'?: string;
}

export const CodeInput = React.forwardRef<HTMLDivElement, CodeInputProps>(
  (
    {
      length = 6,
      value = '',
      onChange,
      onComplete,
      invalid,
      disabled,
      autoFocus,
      className,
      inputMode = 'numeric',
      id,
      'aria-label': ariaLabel = 'Verification code',
    },
    ref,
  ) => {
    const refs = React.useRef<(HTMLInputElement | null)[]>([]);
    const chars = Array.from({ length }, (_, i) => value[i] ?? '');

    const update = (next: string) => {
      const cleaned =
        inputMode === 'numeric' ? next.replace(/\D/g, '').slice(0, length) : next.slice(0, length);
      onChange?.(cleaned);
      if (cleaned.length === length) onComplete?.(cleaned);
    };

    const setChar = (idx: number, ch: string) => {
      const arr = chars.slice();
      arr[idx] = ch;
      update(arr.join(''));
      if (ch && idx < length - 1) refs.current[idx + 1]?.focus();
    };

    React.useEffect(() => {
      if (autoFocus) refs.current[0]?.focus();
    }, [autoFocus]);

    return (
      <div
        ref={ref}
        id={id}
        role="group"
        aria-label={ariaLabel}
        className={cn('flex w-full max-w-full gap-1.5 sm:inline-flex sm:w-auto', className)}
      >
        {chars.map((c, i) => (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="text"
            inputMode={inputMode}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            aria-label={`Digit ${i + 1}`}
            maxLength={1}
            value={c}
            onChange={(e) => {
              const v = e.target.value;
              if (v.length > 1) {
                update(v);
                refs.current[Math.min(length - 1, i + v.length)]?.focus();
              } else {
                setChar(i, v);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !chars[i] && i > 0) {
                refs.current[i - 1]?.focus();
              } else if (e.key === 'ArrowLeft' && i > 0) {
                refs.current[i - 1]?.focus();
              } else if (e.key === 'ArrowRight' && i < length - 1) {
                refs.current[i + 1]?.focus();
              }
            }}
            onPaste={(e) => {
              e.preventDefault();
              update(e.clipboardData.getData('text'));
            }}
            className={cn(
              'h-11 w-9 min-w-0 max-w-9 flex-1 rounded-[var(--radius-md)] border bg-[var(--color-surface)] text-center text-base font-medium text-[var(--color-fg)] sm:flex-none',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
              'disabled:cursor-not-allowed disabled:opacity-60',
              invalid ? 'border-[var(--color-danger)]' : 'border-[var(--color-border)]',
            )}
          />
        ))}
      </div>
    );
  },
);
CodeInput.displayName = 'CodeInput';
